import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"
import { ArrowDown } from "lucide-react"

import {
  getResumableHistoryAnalysisToolCalls,
  type ConversationPartApiResponse,
} from "@f2e/ui/chat-v2"
import { ScrollArea, Button } from "@f2e/ui/components/base"
import {
  type FileBadgeData,
  type HistoryAnalysisCancelHandler,
  type MessageBlockExtension,
} from "@f2e/ui/components/Chat"
import { cn } from "@f2e/ui/lib/utils"

import { CopilotConfig } from "@/types"
import { getCopilotPageKey } from "@/components/Copilot/pageKey"
import {
  RewriteAlertDialog,
  WebConversationPartPanel,
} from "@/components/Conversation"

import { applySuggestionExtension } from "./extensions/applySuggestionExtension"

const isVisibleCopilotAttachment = (file: FileBadgeData) =>
  file.attachmentType !== "page_snapshot"

interface CopilotChatProps {
  className?: string
  parts: ConversationPartApiResponse[]
  config?: CopilotConfig
  isAnswering?: boolean
  isLoading?: boolean
  onSuggestedPromptClick?: (prompt: string) => void
  onRegenerateGroup?: (groupId: string) => void | Promise<void>
  onCancelHistoryAnalysis?: HistoryAnalysisCancelHandler
  messageBlockExtensions?: MessageBlockExtension[]
}

const CopilotChat = ({
  className,
  parts,
  config,
  isAnswering = false,
  isLoading = false,
  onSuggestedPromptClick,
  onRegenerateGroup,
  onCancelHistoryAnalysis,
  messageBlockExtensions: messageBlockExtensionsProp,
}: CopilotChatProps) => {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const pendingRegenerateGroupIdRef = useRef<string | null>(null)
  const [showRewriteAlertDialog, setShowRewriteAlertDialog] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const prevAnsweringRef = useRef<boolean>(false)
  const location = useLocation()

  const hasMessages = parts.length > 0
  const showLoading = isLoading && !hasMessages
  const shouldResumeHistoryAnalysis = useMemo(
    () => getResumableHistoryAnalysisToolCalls(parts).length > 0,
    [parts]
  )

  const pageKey = useMemo(
    () => getCopilotPageKey(location.pathname, location.search),
    [location.pathname, location.search]
  )

  const messageBlockExtensions = useMemo(
    () =>
      messageBlockExtensionsProp ?? [
        applySuggestionExtension as MessageBlockExtension,
      ],
    [messageBlockExtensionsProp]
  )

  const handleRegenerateRequest = useCallback((groupId: string) => {
    pendingRegenerateGroupIdRef.current = groupId
    setShowRewriteAlertDialog(true)
  }, [])

  const handleRewriteAlertOpenChange = useCallback((open: boolean) => {
    setShowRewriteAlertDialog(open)
    if (!open) {
      pendingRegenerateGroupIdRef.current = null
    }
  }, [])

  const handleRewriteAlertConfirm = useCallback(() => {
    const groupId = pendingRegenerateGroupIdRef.current
    pendingRegenerateGroupIdRef.current = null
    setShowRewriteAlertDialog(false)
    if (!groupId) return
    void onRegenerateGroup?.(groupId)
  }, [onRegenerateGroup])

  const suggestions = useMemo(() => {
    const byPage = config?.suggestionQuestionsByPage

    if (pageKey && byPage) {
      const keysToTry =
        pageKey === "workspace.agent_playground.new_agent"
          ? [pageKey, "workspace.agent_playground"]
          : [pageKey]

      for (const key of keysToTry) {
        if (Array.isArray(byPage[key]) && byPage[key].length > 0) {
          return byPage[key]
        }
      }
    }

    return config?.suggestionQuestions || []
  }, [config, pageKey])

  // Pause auto-scroll when user scrolls away from bottom
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const viewport = root.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) as HTMLElement | null
    if (!viewport) return

    // Set overscroll-behavior to prevent scroll chaining at CSS level
    viewport.style.overscrollBehavior = "contain"

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
      if (distanceFromBottom > 24) {
        setAutoScroll(false)
      } else if (distanceFromBottom <= 8) {
        // Re-enable auto-scroll when user scrolls back to bottom
        setAutoScroll(true)
      }
    }

    // Prevent scroll chaining/bubbling when content is not scrollable or at boundaries
    const onWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = viewport
      const isScrollable = scrollHeight > clientHeight

      // If not scrollable, prevent the event from propagating
      if (!isScrollable) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      // At top boundary and scrolling up
      if (scrollTop <= 0 && e.deltaY < 0) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      // At bottom boundary and scrolling down
      if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
    }

    // Handle touch scrolling for mobile devices
    let touchStartY = 0
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
    }

    const onTouchMove = (e: TouchEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = viewport
      const isScrollable = scrollHeight > clientHeight
      const touchY = e.touches[0].clientY
      const deltaY = touchStartY - touchY

      // If not scrollable, prevent the event from propagating
      if (!isScrollable) {
        e.preventDefault()
        return
      }

      // At top boundary and scrolling up
      if (scrollTop <= 0 && deltaY < 0) {
        e.preventDefault()
        return
      }

      // At bottom boundary and scrolling down
      if (scrollTop + clientHeight >= scrollHeight && deltaY > 0) {
        e.preventDefault()
        return
      }
    }

    viewport.addEventListener("scroll", onScroll, { passive: true })
    viewport.addEventListener("wheel", onWheel, { passive: false })
    viewport.addEventListener("touchstart", onTouchStart, { passive: true })
    viewport.addEventListener("touchmove", onTouchMove, { passive: false })
    return () => {
      viewport.removeEventListener("scroll", onScroll)
      viewport.removeEventListener("wheel", onWheel)
      viewport.removeEventListener("touchstart", onTouchStart)
      viewport.removeEventListener("touchmove", onTouchMove)
    }
  }, [])

  // Auto-scroll to bottom when messages change if enabled
  useEffect(() => {
    if (!autoScroll) return
    const id = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ block: "end" })
    })
    return () => cancelAnimationFrame(id)
  }, [parts, autoScroll])

  // When a new answering turn starts, re-enable auto-scroll and stick to bottom
  useEffect(() => {
    const prev = prevAnsweringRef.current
    const now = Boolean(isAnswering)
    if (!prev && now) {
      setAutoScroll(true)
      const id = requestAnimationFrame(() => {
        endRef.current?.scrollIntoView({ block: "end" })
      })
      return () => cancelAnimationFrame(id)
    }
    prevAnsweringRef.current = now
  }, [isAnswering])

  // Handle scroll to bottom button click
  const handleScrollToBottom = () => {
    setAutoScroll(true)
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }

  // Show scroll to bottom button when not at bottom and has messages
  const showScrollButton = !autoScroll && hasMessages

  return (
    <div className={cn("relative", className)}>
      <ScrollArea ref={rootRef} className="w-full h-full overflow-hidden">
        <div
          className={cn(
            "grid grid-cols-1 w-full overflow-hidden px-4",
            !hasMessages ? "h-full min-h-[240px]" : "gap-4 py-4"
          )}
          style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
        >
          {!hasMessages ? (
            showLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-muted-foreground">
                  {t("common.loading")}
                </div>
              </div>
            ) : (
              <EmptyState
                suggestions={suggestions}
                onSuggestedPromptClick={onSuggestedPromptClick}
              />
            )
          ) : (
            <WebConversationPartPanel
              parts={parts}
              isAsking={isAnswering}
              assistantActionBarMode="hidden"
              onRegenerateGroup={
                onRegenerateGroup && !shouldResumeHistoryAnalysis
                  ? handleRegenerateRequest
                  : undefined
              }
              onCancelHistoryAnalysis={onCancelHistoryAnalysis}
              showHistoryAnalysisResumeGuidance
              filterAttachment={isVisibleCopilotAttachment}
              messageBlockExtensions={messageBlockExtensions}
            />
          )}

          {/* Sentinel element to allow robust scroll-into-view */}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Scroll to bottom button - centered */}
      {showScrollButton && (
        <Button
          variant="outline"
          size="icon"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 h-8 w-8 rounded-full shadow-md bg-background/95 backdrop-blur-sm border-border/50 hover:bg-accent transition-all"
          onClick={handleScrollToBottom}
          aria-label={t(
            "common.copilot.chat.scrollToBottom",
            "Scroll to bottom"
          )}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}

      <RewriteAlertDialog
        open={showRewriteAlertDialog}
        onOpenChange={handleRewriteAlertOpenChange}
        onConfirm={handleRewriteAlertConfirm}
      />
    </div>
  )
}

interface EmptyStateProps {
  suggestions?: string[]
  onSuggestedPromptClick?: (prompt: string) => void
}

const EmptyState = ({
  suggestions,
  onSuggestedPromptClick,
}: EmptyStateProps) => {
  const { t } = useTranslation()

  const hasSuggestions = suggestions && suggestions.length > 0

  return (
    <div
      className={`flex flex-col items-center gap-6 text-center ${
        hasSuggestions ? "justify-start py-2" : "justify-center h-full"
      }`}
    >
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t("common.copilot.chat.welcomeTitle")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("common.copilot.chat.welcomeDescription")}
        </p>
      </div>
      {hasSuggestions && (
        <div className="flex flex-col gap-2 w-full max-w-sm">
          <p className="text-xs font-medium text-muted-foreground">
            {t("common.copilot.chat.tryAsking")}
          </p>
          <div className="grid grid-cols-1 gap-2">
            {suggestions.map((prompt, index) => (
              <button
                key={index}
                className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-left transition-colors hover:bg-muted"
                onClick={() => onSuggestedPromptClick?.(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CopilotChat
